/**
 * PageThemeConfigContext - Single source of truth for page-level theming
 *
 * This is the ONLY theme context needed for page-level customization.
 * ThemeProvider reads from this to generate custom Chakra themes and set color mode.
 *
 * Architecture:
 * ```
 * _app.tsx
 *   └── PageThemeConfigProvider  ← This context (can receive initialThemeConfig from GSSP)
 *         └── ThemeProvider      ← Reads config, generates theme, sets color mode
 *               └── Page content
 * ```
 *
 * Usage:
 * ```tsx
 * // Option 1: Pass initial config from GSSP (preferred - no flash)
 * <PageThemeConfigProvider initialThemeConfig={pageProps.initialThemeConfig}>
 *
 * // Option 2: Set via hook (useLayoutEffect — blocks paint until applied)
 * useSetPageThemeConfig({
 *   colorMode: "dark",
 *   primaryColor: "#FF5500",
 * });
 * ```
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useLayoutEffect,
  useEffect,
  ReactNode,
  useMemo,
} from "react";
import {
  DEFAULT_SHOWPASS_THEME_CONFIG,
  LayoutThemeConfig,
} from "@shared/config/layout.config";
import { useRouter } from "next/router";
import { BRANDED_PUBLIC_PATHNAMES } from "@app-contexts/public/constants/public-routes";

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

interface PageThemeConfigContextValue {
  themeConfig: LayoutThemeConfig | undefined;
  setThemeConfig: (config: LayoutThemeConfig | undefined) => void;
}

const PageThemeConfigContext = createContext<
  PageThemeConfigContextValue | undefined
>(undefined);

interface PageThemeConfigProviderProps {
  children: ReactNode;
  /** Initial theme config from GSSP - applied immediately on first render.
   *  null means "explicitly no branding" (e.g. checkout with non-branded basket). */
  initialThemeConfig?: LayoutThemeConfig | null;
}

/**
 * Provider - place in _app.tsx above ThemeProvider
 * Can receive initialThemeConfig from GSSP to avoid flash of unthemed content
 */
export function PageThemeConfigProvider({
  children,
  initialThemeConfig,
}: PageThemeConfigProviderProps) {
  const [themeConfig, setThemeConfigState] = useState<
    LayoutThemeConfig | undefined
  >(initialThemeConfig ?? undefined);

  const router = useRouter();

  const pageIsBranded = useMemo(() => {
    const normalizedPathname = router.pathname.endsWith("/")
      ? router.pathname
      : `${router.pathname}/`;
    return BRANDED_PUBLIC_PATHNAMES.includes(normalizedPathname);
  }, [router.pathname]);

  // Sync state when initialThemeConfig prop changes (client-side navigation).
  // Always apply the new value — including undefined/null — so that stale
  // theme configs from a previous branded page don't leak into the next page.
  // The pageIsBranded guard on the context value handles non-branded pages,
  // and BrandedCheckoutPage's useSetPageThemeConfig will re-apply the correct
  // config once the basket-aware branding resolves.
  //
  // useEffect (not useLayoutEffect) because the initial render is already
  // correct via the useState initializer. This only fires on client-side
  // navigations and avoids the SSR useLayoutEffect warning.
  useEffect(() => {
    setThemeConfigState(initialThemeConfig ?? undefined);
  }, [initialThemeConfig]);

  const setThemeConfig = useCallback(
    (config: LayoutThemeConfig | undefined) => {
      setThemeConfigState(config);
    },
    []
  );

  return (
    <PageThemeConfigContext.Provider
      value={{
        themeConfig: pageIsBranded
          ? themeConfig
          : DEFAULT_SHOWPASS_THEME_CONFIG, // forced light mode for showpass pages

        setThemeConfig,
      }}
    >
      {children}
    </PageThemeConfigContext.Provider>
  );
}

/**
 * Hook for ThemeProvider to read current config
 */
export function usePageThemeConfig(): LayoutThemeConfig | undefined {
  const context = useContext(PageThemeConfigContext);
  return context?.themeConfig;
}

/**
 * True when the page theme sets a custom `primaryColor` (branded surfaces).
 * Web `Button` uses this by default; pass `autoContrast={false}` on a button to opt out.
 */
export function useAutoContrast(): boolean {
  const themeConfig = usePageThemeConfig();
  return Boolean(themeConfig?.primaryColor);
}

/**
 * Hook for pages to set their theme config
 * Sets both custom colors AND color mode in one call
 *
 * Uses useLayoutEffect so the update is flushed synchronously before
 * the browser paints, preventing a flash of the wrong theme.
 *
 * Note: No cleanup on unmount - the PageThemeConfigProvider handles
 * non-branded pages via the pageIsBranded check. Cleanup was removed
 * because it caused a flash during client-side navigation between
 * branded pages.
 */
export function useSetPageThemeConfig(
  config: LayoutThemeConfig | undefined
): void {
  const context = useContext(PageThemeConfigContext);

  useIsomorphicLayoutEffect(() => {
    if (context && config) {
      context.setThemeConfig(config);
    }
  }, [context, config]);
}
